import { z } from 'zod';
import {
  DAY_END_PATTERN,
  ROUTING_MODES,
  WORK_PROOF_MODES,
} from '../../src/domain/access/settings';
import { RowVersionSchema } from './common.schema';

/**
 * The lab settings, `GET /settings` and `PUT /settings`. Section 2.3 of the
 * system test handover.
 *
 * `routing` is how a case leaves a station: `automatic` follows the three
 * production routes (handoff 11.2); `manual` requires `nextStage` on every
 * `POST /kits/{kitId}/complete`. The object also rides on the session, beside
 * the geofence, so the job view knows the mode without a second call.
 */
export const RoutingModeSchema = z.enum(ROUTING_MODES);

/**
 * Proof of work: `off`, `optional` or `mandatory`. On `mandatory` the server
 * refuses `POST /kits/{kitId}/complete` without a proof on the open entry.
 * Section 2 of the request of 13 September 2026.
 */
export const WorkProofModeSchema = z.enum(WORK_PROOF_MODES);

/**
 * When the lab's day ends, `HH:mm` on the lab's clock. Every clock still
 * running is stopped at that moment by the API, on the holder's behalf and with
 * their time kept, and `GET /reports/staff` counts no run past it. Null switches
 * the rule off. Request of 25 September 2026.
 */
export const DayEndSchema = z.string().regex(DAY_END_PATTERN, 'must look like 18:00');

export const LabSettingsSchema = z.object({
  routing: RoutingModeSchema,
  workProof: WorkProofModeSchema,
  dayEndsAt: DayEndSchema.nullable(),
  /**
   * When a clock started AFTER the day end stops, "HH:mm", or null to let it
   * run to the next day end. It stops at this time or at the next day end,
   * whichever comes first, and the staff report counts it no further. Midnight
   * until the lab says otherwise. Section 17 of the review of 2 October 2026.
   */
  afterHoursEndsAt: DayEndSchema.nullable(),
  /**
   * Whether a case at Final Check may be taken over, after the warning. Off
   * unless management switches it on; while off, `POST /kits/{kitId}/start`
   * with `takeOver` on a case at Final Check is a 409. Each change is logged.
   * Section 9 of the request of 5 October 2026.
   */
  takeOverAtFinalCheck: z.boolean().optional(),
  rowVersion: RowVersionSchema,
});

/**
 * The body of `PUT /settings`. The rowVersion travels as If-Match instead. Any
 * of the settings, so a screen sends the one it changed; a field left out is
 * left as it was.
 */
export const SaveLabSettingsSchema = LabSettingsSchema.omit({ rowVersion: true }).partial();

export type LabSettingsPayload = z.infer<typeof LabSettingsSchema>;
